import { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Card, Typography, Skeleton, Tooltip } from '@mui/material';
import intl from 'components/i18n/ReactIntlWrapper';
import { useApi } from 'api/ApiProvider';
import Api from 'axiosApi/api';
import { useToastMessageQueue } from 'components/ToastMessages/ToastMessageProvider';
import { handleAPIError } from 'common/errorHandler';
import { getUserEntity } from 'common/UserEntityProvider';
import { ENTITY_TYPE } from 'common/constants';
import { HRApiWrapper } from 'api/HRApiWrapper';

const COLORS_BY_POSITION = [
  '#aec6e8', // light blue
  '#ffd966', // yellow
  '#92d050', // green
  '#e07b54', // orange
  '#b39ddb', // lavender
  '#80cbc4', // teal
  '#f48fb1', // pink
  '#ffcc80', // peach
  '#a5d6a7', // mint
  '#ef9a9a', // coral
];

const CATEGORY_HEADER_HEIGHT = 36;
const SKILL_HEADER_HEIGHT = 120;
const CELL_SIZE = 28;
const ROLE_COL_WIDTH = 160;
const SENIORITY_COL_WIDTH = 130;

interface ProfileSkillEntry {
  id: number;
  idSkillLevel: number;
}

const MySkillsPage: React.FC = () => {
  const api: Api = useApi();
  const { employeeApi } = api;
  const hrApi = new HRApiWrapper(employeeApi);
  const toast = useToastMessageQueue();
  const userEntity = getUserEntity();

  const [categories, setCategories] = useState<any[]>([]);
  const [skills, setSkills] = useState<any[]>([]);
  const [levels, setLevels] = useState<any[]>([]);
  const [levelColorMap, setLevelColorMap] = useState<Map<number, string>>(new Map());
  const [levelLabelMap, setLevelLabelMap] = useState<Map<number, string>>(new Map());
  const [profiles, setProfiles] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [pending, setPending] = useState<Set<string>>(new Set());
  const [skillMap, setSkillMap] = useState<Map<string, ProfileSkillEntry>>(new Map());

  const levelIds = useRef<number[]>([]);

  const load = useCallback(async () => {
    if (!userEntity?.entityId || userEntity.entityType !== ENTITY_TYPE.EMPLOYEE) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [catRes, skillRes, levelRes, profileRes] = await Promise.all([
        hrApi.getSkillCategories(),
        hrApi.getSkills('1', 1, 1000),
        hrApi.getSkillLevels(),
        hrApi.getProfilesByEmployee('1', userEntity.entityId),
      ]);

      const cats: any[] = catRes.data?.data ?? [];
      const allSkills: any[] = skillRes.data?.data ?? [];
      const allLevels: any[] = levelRes.data?.data ?? [];
      const allProfiles: any[] = profileRes.data?.data ?? [];

      const sortedLevels = [...allLevels].sort((a, b) => a.id - b.id);
      levelIds.current = sortedLevels.map((l) => l.id);

      const colorMap = new Map<number, string>();
      const labelMap = new Map<number, string>();
      sortedLevels.forEach((l, idx) => {
        colorMap.set(l.id, COLORS_BY_POSITION[idx] ?? '#aec6e8');
        labelMap.set(l.id, l.name);
      });
      setLevels(sortedLevels);
      setLevelColorMap(colorMap);
      setLevelLabelMap(labelMap);

      const skillsByCat = new Map<number, any[]>();
      for (const sk of allSkills) {
        if (!sk.isDeleted) {
          const list = skillsByCat.get(sk.idSkillCategory) ?? [];
          list.push(sk);
          skillsByCat.set(sk.idSkillCategory, list);
        }
      }
      const activeCats = cats
        .filter((c) => !c.isDeleted && skillsByCat.has(c.id))
        .map((c) => ({ ...c, skills: skillsByCat.get(c.id) ?? [] }));

      const orderedSkills: any[] = [];
      for (const cat of activeCats) orderedSkills.push(...cat.skills);

      const map = new Map<string, ProfileSkillEntry>();
      for (const p of allProfiles) {
        for (const ps of p.profileSkills ?? []) {
          if (!ps.isDeleted) {
            map.set(`${p.id}_${ps.idSkill}`, { id: ps.id, idSkillLevel: ps.idSkillLevel });
          }
        }
      }

      setCategories(activeCats);
      setSkills(orderedSkills);
      setProfiles(allProfiles);
      setSkillMap(map);
    } catch (e) {
      handleAPIError(e, toast, {});
    } finally {
      setLoading(false);
    }
  }, [userEntity?.entityId]);

  useEffect(() => { load(); }, [load]);

  const handleCellClick = useCallback(async (profileId: number, skillId: number) => {
    const key = `${profileId}_${skillId}`;
    if (pending.has(key)) return;

    const existing = skillMap.get(key);
    const ids = levelIds.current;
    if (!ids.length) return;

    let nextLevelId: number | null = null;
    if (!existing) {
      nextLevelId = ids[0];
    } else {
      const idx = ids.indexOf(existing.idSkillLevel);
      nextLevelId = (idx === -1 || idx === ids.length - 1) ? null : ids[idx + 1];
    }

    setPending((prev) => new Set(prev).add(key));
    const prevMap = new Map(skillMap);

    if (nextLevelId === null && existing) {
      const newMap = new Map(skillMap);
      newMap.delete(key);
      setSkillMap(newMap);
    } else if (nextLevelId !== null) {
      const newMap = new Map(skillMap);
      newMap.set(key, { id: existing?.id ?? 0, idSkillLevel: nextLevelId });
      setSkillMap(newMap);
    }

    try {
      if (!existing && nextLevelId !== null) {
        const res = await hrApi.addSkillToProfile('1', { idProfile: profileId, idSkill: skillId, idSkillLevel: nextLevelId });
        const created = res.data?.data;
        if (created) {
          setSkillMap((prev) => {
            const m = new Map(prev);
            m.set(key, { id: created.id, idSkillLevel: created.idSkillLevel });
            return m;
          });
        }
      } else if (existing && nextLevelId !== null) {
        await hrApi.updateProfileSkill('1', { id: existing.id, idSkillLevel: nextLevelId });
      } else if (existing && nextLevelId === null) {
        await hrApi.removeSkillFromProfile('1', existing.id);
      }
    } catch (e) {
      setSkillMap(prevMap);
      handleAPIError(e, toast, {});
    } finally {
      setPending((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  }, [skillMap, pending, hrApi, toast]);

  if (!userEntity?.entityId || userEntity.entityType !== ENTITY_TYPE.EMPLOYEE) {
    return (
      <Card sx={{ m: 2, p: 3 }}>
        <Typography color="text.secondary">
          {intl.get('hr.mySkills.notEmployee') || 'This page is only available for employee accounts.'}
        </Typography>
      </Card>
    );
  }

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Skeleton variant="rectangular" height={40} sx={{ mb: 1 }} />
        <Skeleton variant="rectangular" height={300} />
      </Box>
    );
  }

  if (profiles.length === 0) {
    return (
      <Card sx={{ m: 2, p: 3 }}>
        <Typography color="primary" variant="h2" component="h2" sx={{ mb: 2 }}>
          {intl.get('mainNavigation.mySkills') || 'My Skills'}
        </Typography>
        <Typography color="text.secondary">
          {intl.get('hr.mySkills.noProfiles') || 'No profiles found. Ask your HR manager to create a profile for you.'}
        </Typography>
      </Card>
    );
  }

  const stickyHeaderBase = { position: 'sticky', zIndex: 2, backgroundColor: '#fff' };

  return (
    <Card sx={{ m: 2 }}>
      <Box sx={{ p: 3 }}>
        <Typography color="primary" variant="h2" component="h2" sx={{ mb: 3 }}>
          {intl.get('mainNavigation.mySkills') || 'My Skills'}
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
          {levels.map((l, idx) => (
            <Box key={l.id} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 16, height: 16, backgroundColor: COLORS_BY_POSITION[idx] ?? '#aec6e8', border: '1px solid #ccc', borderRadius: 0.5 }} />
              <Typography variant="caption">{l.name}</Typography>
            </Box>
          ))}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 16, height: 16, backgroundColor: '#fff', border: '1px solid #ccc', borderRadius: 0.5 }} />
            <Typography variant="caption">{intl.get('common.none') || 'None'}</Typography>
          </Box>
        </Box>

        <Box sx={{ overflow: 'auto', maxHeight: 'calc(100vh - 260px)', border: '1px solid #e0e0e0', borderRadius: 1 }}>
          <table style={{ borderCollapse: 'collapse', tableLayout: 'fixed' }}>
            <thead>
              <tr>
                <th
                  style={{
                    ...stickyHeaderBase as any,
                    top: 0, left: 0, zIndex: 3,
                    width: ROLE_COL_WIDTH, minWidth: ROLE_COL_WIDTH,
                    height: CATEGORY_HEADER_HEIGHT,
                    background: '#fff', border: '1px solid #e0e0e0',
                  }}
                />
                <th
                  style={{
                    ...stickyHeaderBase as any,
                    top: 0, left: ROLE_COL_WIDTH, zIndex: 3,
                    width: SENIORITY_COL_WIDTH, minWidth: SENIORITY_COL_WIDTH,
                    height: CATEGORY_HEADER_HEIGHT,
                    background: '#fff', border: '1px solid #e0e0e0',
                  }}
                />
                {categories.map((cat) => (
                  <th
                    key={cat.id}
                    colSpan={cat.skills.length}
                    style={{
                      position: 'sticky', top: 0, zIndex: 2,
                      background: '#424242', color: '#fff',
                      fontWeight: 700, fontSize: 12, textAlign: 'center',
                      height: CATEGORY_HEADER_HEIGHT,
                      border: '1px solid #616161', padding: '0 8px', whiteSpace: 'nowrap',
                    }}
                  >
                    {cat.name}
                  </th>
                ))}
              </tr>
              <tr>
                <th
                  style={{
                    position: 'sticky', top: CATEGORY_HEADER_HEIGHT, left: 0, zIndex: 3,
                    width: ROLE_COL_WIDTH, minWidth: ROLE_COL_WIDTH,
                    height: SKILL_HEADER_HEIGHT,
                    background: '#f5f5f5', border: '1px solid #e0e0e0',
                    fontSize: 12, fontWeight: 600,
                    paddingLeft: 8, textAlign: 'left', verticalAlign: 'bottom', paddingBottom: 6,
                  }}
                >
                  {intl.get('hr.profile.role') || 'Role'}
                </th>
                <th
                  style={{
                    position: 'sticky', top: CATEGORY_HEADER_HEIGHT, left: ROLE_COL_WIDTH, zIndex: 3,
                    width: SENIORITY_COL_WIDTH, minWidth: SENIORITY_COL_WIDTH,
                    height: SKILL_HEADER_HEIGHT,
                    background: '#f5f5f5', border: '1px solid #e0e0e0',
                    fontSize: 12, fontWeight: 600,
                    paddingLeft: 8, textAlign: 'left', verticalAlign: 'bottom', paddingBottom: 6,
                  }}
                >
                  {intl.get('hr.profile.seniority') || 'Seniority'}
                </th>
                {skills.map((sk) => (
                  <th
                    key={sk.id}
                    style={{
                      position: 'sticky', top: CATEGORY_HEADER_HEIGHT, zIndex: 2,
                      width: CELL_SIZE, minWidth: CELL_SIZE, height: SKILL_HEADER_HEIGHT,
                      background: '#f5f5f5', border: '1px solid #e0e0e0', padding: 0,
                    }}
                  >
                    <div
                      style={{
                        writingMode: 'vertical-rl', transform: 'rotate(180deg)',
                        fontSize: 11, fontWeight: 500,
                        padding: '4px 0', whiteSpace: 'nowrap',
                        overflow: 'hidden', textOverflow: 'ellipsis',
                        maxHeight: SKILL_HEADER_HEIGHT - 8,
                      }}
                    >
                      {sk.name}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {profiles.map((profile) => (
                <tr key={profile.id}>
                  <td
                    style={{
                      position: 'sticky', left: 0, zIndex: 1, background: '#fff',
                      border: '1px solid #e0e0e0', fontSize: 12,
                      width: ROLE_COL_WIDTH, minWidth: ROLE_COL_WIDTH,
                      padding: '0 8px', whiteSpace: 'nowrap',
                      overflow: 'hidden', textOverflow: 'ellipsis',
                      height: CELL_SIZE + 2,
                    }}
                  >
                    {profile.role?.name ?? '-'}
                  </td>
                  <td
                    style={{
                      position: 'sticky', left: ROLE_COL_WIDTH, zIndex: 1, background: '#fff',
                      border: '1px solid #e0e0e0', fontSize: 11, color: '#666',
                      width: SENIORITY_COL_WIDTH, minWidth: SENIORITY_COL_WIDTH,
                      padding: '0 8px', whiteSpace: 'nowrap',
                      overflow: 'hidden', textOverflow: 'ellipsis',
                    }}
                  >
                    {profile.seniority?.name ?? ''}
                  </td>
                  {skills.map((sk) => {
                    const key = `${profile.id}_${sk.id}`;
                    const entry = skillMap.get(key);
                    const levelId = entry?.idSkillLevel;
                    const isPending = pending.has(key);
                    const bg = levelId ? (levelColorMap.get(levelId) ?? '#fff') : '#fff';
                    const label = levelId ? (levelLabelMap.get(levelId) ?? '') : '';
                    return (
                      <Tooltip key={sk.id} title={label ? `${sk.name}: ${label}` : sk.name} placement="top">
                        <td
                          onClick={() => handleCellClick(profile.id, sk.id)}
                          style={{
                            width: CELL_SIZE, minWidth: CELL_SIZE, height: CELL_SIZE + 2,
                            backgroundColor: bg,
                            border: '1px solid #e0e0e0',
                            cursor: 'pointer',
                            opacity: isPending ? 0.5 : 1,
                            transition: 'background-color 0.15s',
                          }}
                        />
                      </Tooltip>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </Box>
      </Box>
    </Card>
  );
};

export default MySkillsPage;
